import { Dispatch } from 'redux';
import React from 'react';
import axios from 'axios';
import { v1 as createGUID } from 'uuid';
import {
  MINIMUM_TYPING_INTERVAL_IN_MILLISECONDS,
  MAXIMUM_INT64,
  PAGE_SIZE,
  INITIAL_MESSAGES_SIZE,
  OK,
  DEV_GLOBAL_URL
} from '../constants';
import { setChatClient, setContosoUser, setContosoUsers } from './actions/ContosoClientAction';
import { setReceipts } from './actions/ConversationsAction';
import { setMessages, setTypingNotifications, setTypingUsers, setFailedMessages } from './actions/MessagesAction';
import { setThreadId, setThreadTopicName } from './actions/ThreadAction';
import { setThreadMembers, setThreadMembersError, setRemovedFromThread } from './actions/ThreadMembersAction';
import { User } from './reducers/ContosoClientReducers';
import { State } from './reducers/index';
import { ClientChatMessage } from './reducers/MessagesReducer';
import {
  compareMessages,
  convertToClientChatMessage,
  createNewClientChatMessage,
  isUserMatchingIdentity
} from '../Utils/Utils';

import {
  localStorageAvailable,
  getDisplayFirstNameFromLocalStorage,
  getDisplayLastNameFromLocalStorage,
  getDisplayEmailFromLocalStorage,
  getDisplayMobileFromLocalStorage
} from '../Utils/localStorage';
import {
  ChatClient,
  ChatThreadClient,
  SendReadReceiptRequest,
  ChatMessageReadReceipt,
  ChatMessage,
  ChatParticipant
} from '@azure/communication-chat';
import {
  AzureCommunicationTokenCredential,
  CommunicationTokenRefreshOptions,
  CommunicationUserIdentifier
} from '@azure/communication-common';
import {
  ChatThreadPropertiesUpdatedEvent,
  CommunicationUserKind,
  ParticipantsAddedEvent,
  ParticipantsRemovedEvent
} from '@azure/communication-signaling';

// This function sets up the user to chat with the thread
const addUserToThread = (displayName: string, emoji: string) => async (dispatch: Dispatch, getState: () => State) => {
  const state: State = getState();
  const threadId: string = state.thread.threadId ? state.thread.threadId : (sessionStorage.getItem('threadId') || '');
  if (threadId === undefined) {
    console.error('Thread Id not created yet');
    return;
  }
  const getEnvironmentUrl = async () => {
    try {
      const getRequestOptions = {
        method: 'GET'
      };
      const response = await fetch(DEV_GLOBAL_URL + 'CommunicationServices/getEnvironmentUrl', getRequestOptions);
      return response.json();
    } catch (error) {
      console.error('Failed at getting environment url, Error: ', error);
    }
  };
  // get environment url from server
  const environmentUrl = await getEnvironmentUrl();

  if (environmentUrl === undefined && !environmentUrl.success && !environmentUrl.data) {
    console.error('unable to get environment url from server');
    return;
  }
  // create our user
  const userToken = await getToken();
  // console.log(userToken, 'userToken')

  if (userToken === undefined && userToken.data === undefined && !userToken.success) {
    console.error('unable to get a token');
    return;
  }
  const userTokenData = userToken.data;
  const user = userTokenData.user as CommunicationUserIdentifier;
  // console.log(userTokenData, 'userTokenData')
  const options: CommunicationTokenRefreshOptions = {
    token: userTokenData.token,
    tokenRefresher: () => refreshTokenAsync(user.communicationUserId),
    refreshProactively: true
  };
  // console.log(environmentUrl, 'environmentUrl')

  const userAccessTokenCredentialNew = new AzureCommunicationTokenCredential(options);
  const chatClient = new ChatClient(environmentUrl.data, userAccessTokenCredentialNew);

  // set emoji for the user
  // setEmoji(user.communicationUserId, emoji);

  // subscribe for message, typing indicator, and read receipt
  const chatThreadClient = await chatClient.getChatThreadClient(threadId);
  subscribeForMessage(chatClient, dispatch, getState);
  subscribeForTypingIndicator(chatClient, dispatch);
  subscribeForReadReceipt(chatClient, chatThreadClient, dispatch);
  subscribeForChatParticipants(chatClient, user.communicationUserId, dispatch, getState);
  subscribeForTopicUpdated(chatClient, dispatch, getState);
  dispatch(setThreadId(threadId));
  dispatch(setContosoUser(user.communicationUserId, userTokenData.token, displayName));
  dispatch(setChatClient(chatClient));

  await addThreadMemberHelper(
    threadId,
    {
      identity: user.communicationUserId,
      token: userTokenData.token,
      displayName: displayName,
      memberRole: 'User'
    },
    dispatch
  );

  await getThreadInformation(chatClient, dispatch, getState);
  await getMessages(chatClient, dispatch, getState);
};

const subscribeForTypingIndicator = async (chatClient: ChatClient, dispatch: Dispatch) => {
  await chatClient.startRealtimeNotifications();
  chatClient.on('typingIndicatorReceived', async (event) => {
    const fromId = (event.sender as CommunicationUserKind).communicationUserId;
    const typingNotification = {
      from: fromId,
      originalArrivalTime: event.receivedOn,
      recipientId: (event.recipient as CommunicationUserKind).communicationUserId,
      threadId: event.threadId,
      version: event.version
    };
    dispatch(setTypingNotifications(fromId, typingNotification));
  });
};

const subscribeForMessage = async (chatClient: ChatClient, dispatch: Dispatch, getState: () => State) => {
  await chatClient.startRealtimeNotifications();
  chatClient.on('chatMessageReceived', async (event) => {
    const state: State = getState();
    const messages: ClientChatMessage[] = state.chat.messages !== undefined ? state.chat.messages : [];
    if (!isUserMatchingIdentity(event.sender, state.contosoClient.user.identity)) {
      const clientChatMessage = {
        sender: event.sender,
        id: event.id,
        senderDisplayName: event.senderDisplayName,
        createdOn: event.createdOn,
        content: { message: event.message }
      };

      messages.push(clientChatMessage);
      dispatch(setMessages(messages.sort(compareMessages)));
    }
  });
};

const subscribeForReadReceipt = async (
  chatClient: ChatClient,
  chatThreadClient: ChatThreadClient,
  dispatch: Dispatch
) => {
  await chatClient.startRealtimeNotifications();
  chatClient.on('readReceiptReceived', async (event) => {
    let receipts: ChatMessageReadReceipt[] = [];
    for await (let page of chatThreadClient.listReadReceipts().byPage()) {
      for (const receipt of page) {
        receipts.push(receipt);
      }
    }
    dispatch(setReceipts(receipts));
  });
};

const subscribeForChatParticipants = async (
  chatClient: ChatClient,
  identity: string,
  dispatch: Dispatch,
  getState: () => State
) => {
  chatClient.on('participantsRemoved', async (event: ParticipantsRemovedEvent) => {
    const state = getState();
    let participants: ChatParticipant[] = [];
    for (let chatParticipant of event.participantsRemoved) {
      // if you are in the list, remove yourself from the chat
      if (isUserMatchingIdentity(chatParticipant.id, identity)) {
        dispatch(setRemovedFromThread(true));
        return;
      }
    }

    const originalParticipants = state.threadMembers.threadMembers;
    for (var i = 0; i < originalParticipants.length; i++) {
      const participantId = (originalParticipants[i].id as CommunicationUserIdentifier).communicationUserId;
      if (
        event.participantsRemoved.filter((chatParticipant) => isUserMatchingIdentity(chatParticipant.id, participantId))
          .length === 0
      ) {
        participants.push(originalParticipants[i]);
      }
    }

    dispatch(setThreadMembers(participants));
  });

  chatClient.on('participantsAdded', async (event: ParticipantsAddedEvent) => {
    const state = getState();
    let participants: ChatParticipant[] = [...state.threadMembers.threadMembers];

    // there is a chance that the participant added is you and so there is a chance that you can come in as a
    // new participant as well
    const addedParticipants = event.participantsAdded.map((chatParticipant: ChatParticipant) => {
      return {
        id: chatParticipant.id,
        displayName: chatParticipant.displayName,
        shareHistoryTime: new Date(chatParticipant?.shareHistoryTime || new Date())
      };
    });

    // add participants not in the list
    for (var j = 0; j < event.participantsAdded.length; j++) {
      const addedParticipant = event.participantsAdded[j];
      const id = (addedParticipant.id as CommunicationUserIdentifier).communicationUserId;
      if (
        participants.filter((participant: ChatParticipant) => isUserMatchingIdentity(participant.id, id)).length === 0
      ) {
        participants.push(addedParticipant);
      }
    }

    // also make sure we get the emojis for the new participants
    let users = Object.assign({}, state.contosoClient.users);
    for (var i = 0; i < addedParticipants.length; i++) {
      var threadMember = addedParticipants[i];
      var identity = (threadMember.id as CommunicationUserIdentifier).communicationUserId;
      var user = users[identity];
      if (user == null) {
        // var serverUser = await getEmoji(identity);
        var serverUser = { emoji: "🐱" };
        if (serverUser !== undefined) {
          users[identity] = { emoji: serverUser.emoji };
        }
      }
    }

    dispatch(setContosoUsers(users));
    dispatch(setThreadMembers(participants));
  });
};

const subscribeForTopicUpdated = async (chatClient: ChatClient, dispatch: Dispatch, getState: () => State) => {
  chatClient.on('chatThreadPropertiesUpdated', async (e: ChatThreadPropertiesUpdatedEvent) => {
    const state = getState();
    let threadId = state.thread.threadId;

    if (!threadId) {
      console.error('no threadId set');
      return;
    }

    dispatch(setThreadTopicName(e.properties.topic));
  });
};

const sendTypingNotification = () => async (dispatch: Dispatch, getState: () => State) => {
  let state: State = getState();
  let chatClient = state.contosoClient.chatClient;
  if (chatClient === undefined) {
    console.error('Chat Client not created yet');
    return;
  }
  let threadId = state.thread.threadId ? state.thread.threadId : (sessionStorage.getItem('threadId') || '');
  if (threadId === undefined) {
    console.error('Thread Id not created yet');
    return;
  }
  await sendTypingNotificationHelper(await chatClient.getChatThreadClient(threadId));
};

const updateTypingUsers = () => async (dispatch: Dispatch, getState: () => State) => {
  let typingUsers = [];
  let state: State = getState();
  let typingNotifications = state.chat.typingNotifications;
  for (let id in typingNotifications) {
    let typingNotification = typingNotifications[id];
    if (!typingNotification.originalArrivalTime) {
      continue;
    }
    if (shouldDisplayTyping(typingNotification.originalArrivalTime)) {
      let threadMember = state.threadMembers.threadMembers.find((threadMember) =>
        isUserMatchingIdentity(threadMember.id, id)
      );
      if (threadMember) {
        typingUsers.push(threadMember);
      }
    }
  }
  dispatch(setTypingUsers(typingUsers));
};

const shouldDisplayTyping = (lastReceivedTypingEventDate: number) => {
  let currentDate = new Date();
  let timeSinceLastTypingNotificationMs = currentDate.getTime() - lastReceivedTypingEventDate;
  return timeSinceLastTypingNotificationMs <= MINIMUM_TYPING_INTERVAL_IN_MILLISECONDS;
};

const sendMessage = (messageContent: string) => async (dispatch: Dispatch, getState: () => State) => {
  let state: State = getState();
  let chatClient = state.contosoClient.chatClient;

  if (chatClient === undefined) {
    console.error('Chat Client not created yet');
    return;
  }
  let threadId = state.thread.threadId ? state.thread.threadId : (sessionStorage.getItem('threadId') || '');
  if (threadId === undefined) {
    console.error('Thread Id not created yet');
    return;
  }
  let displayName = state.contosoClient.user.displayName;
  let userId = state.contosoClient.user.identity;

  // we use this client message id to have a local id for messages
  // if we fail to send the message we'll at least be able to show that the message failed to send on the client
  let clientMessageId = (Math.floor(Math.random() * MAXIMUM_INT64) + 1).toString(); //generate a random unsigned Int64 number
  let newMessage = createNewClientChatMessage(userId, displayName, clientMessageId, messageContent);

  let messages = getState().chat.messages;
  messages.push(newMessage);
  dispatch(setMessages(messages));

  await sendMessageHelper(
    await chatClient.getChatThreadClient(threadId),
    messageContent,
    displayName,
    clientMessageId,
    dispatch,
    getState
  );
};

const isValidThread = (threadId: string, groupId: string) => async (dispatch: Dispatch) => {
  sessionStorage.setItem('groupId', groupId);
  // if (!threadId) {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   threadId = urlParams.get('threadId') || '';
  // }
  https://localhost:44398/isValidThread/19:cqbsphl2NPYgOOXIK-64je1lFeVUifdtScSDdmWZYFk1@thread.v2
  try {
    // let validationRequestOptions = { method: 'GET' };
    // let validationResponse = await fetch(GLOBAL_URL + 'IsValidThread?threadId=' + threadId, validationRequestOptions);
    // let validationResponse = await fetch(GLOBAL_URL + 'isValidThread/' + threadId, validationRequestOptions);
    let validationRequestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ threadId: threadId })
    };
    let validationResponse = await fetch(DEV_GLOBAL_URL + 'CommunicationServices/IsValidThread', validationRequestOptions);
    // console.log(validationResponse)
    if (validationResponse.status === 200) {
      dispatch(setThreadId(threadId));
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error('Failed at getting isThreadIdValid, Error: ', error);
  }
};
const getMessages = async (chatClient: ChatClient, dispatch: Dispatch, getState: () => State) => {
  let state: State = getState();
  if (chatClient === undefined) {
    console.error('Chat Client not created yet');
    return;
  }
  let threadId = state.thread.threadId ? state.thread.threadId : (sessionStorage.getItem('threadId') || '');
  if (threadId === undefined) {
    console.error('Thread Id not created yet');
    return;
  }
  let messages = await getMessagesHelper(await chatClient.getChatThreadClient(threadId));
  if (messages === undefined) {
    console.error('unable to get messages');
    return;
  }

  const reversedClientChatMessages: ClientChatMessage[] = messages
    .map((message) => convertToClientChatMessage(message))
    .reverse();

  return dispatch(setMessages(reversedClientChatMessages));
};

const saveChatMessages = async (data: any) => {
  try {
    let getTokenRequestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ CommentBody: data, ParentId: sessionStorage.getItem('sfCaseNumber') })
    };
    // console.log(getTokenRequestOptions)

    // var offset = new Date().getTimezoneOffset();// getting offset to make time in gmt+0 zone (UTC) (for gmt+5 offset comes as -300 minutes)
    // var date = new Date();
    // date.setMinutes(date.getMinutes() + offset);// date now in UTC time
    // var easternTimeOffset = -240; //for dayLight saving, Eastern time become 4 hours behind UTC thats why its offset is -4x60 = -240 minutes. So when Day light is not active the offset will be -300
    // date.setMinutes(date.getMinutes() + easternTimeOffset);
    // var converted = new Date(date).getFullYear() + '-' + ('0' + (new Date(date).getMonth() + 1)).slice(-2) + '-' + ('0' + new Date(date).getDate()).slice(-2) + ' ' + ('0' + new Date(date).getHours()).slice(-2) + ':' + ('0' + new Date(date).getMinutes()).slice(-2) + ':' + ('0' + new Date(date).getSeconds()).slice(-2);
    saveAdditonalInfo('Call_End_Time__c', new Date());
    axios.get(DEV_GLOBAL_URL + 'Recording/StopRecording?MeetingId=' + sessionStorage.getItem('meetingId'));
    await fetch(DEV_GLOBAL_URL + 'Salesforce/CreateCaseComment', getTokenRequestOptions);
    // var config = { headers: { 'Content-Type': 'application/json' } };
    // return axios.post(GLOBAL_URL + 'CreateCaseComment', { CommentBody: data, ParentId: sessionStorage.getItem('sfCaseNumber') }, config)
  } catch (error) {
    console.error('Failed at send teams link, Error: ', error);
  }
};

const saveSurvey = async (data: any) => {
  try {
    let getTokenRequestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    };
    // console.log(getTokenRequestOptions)
    // await fetch(GLOBAL_URL + 'CreateSurvey', getTokenRequestOptions);
    var config = { headers: { 'Content-Type': 'application/json' } };
    return axios.post(DEV_GLOBAL_URL + 'Salesforce/CreateSurvey', data, config)
  } catch (error) {
    console.error('Failed at send teams link, Error: ', error);
  }
};

const leaveMeeting = async (end: boolean, join: boolean) => {
  const sfContactId = sessionStorage.getItem('sfContactId');
  const meetingId = sessionStorage.getItem('meetingId');
  var bodyData = {
    SFContactId: sfContactId,
    meetingId: meetingId,
    isEndMeeting: end,
    isRejoinMeetng: join
  }
  try {
    var config = { headers: { 'Content-Type': 'application/json' } };
    return axios.patch(DEV_GLOBAL_URL + 'MeetingUserDetail/UpdateMeetingUserDetails?SFContactId=' + sfContactId, bodyData, config)
  } catch (error) {
    console.error('Failed at send teams link, Error: ', error);
  }
};

const createSendFiles = async (files: any) => {
  var form = new FormData();
  files.forEach((file: any, index: any) => {
    form.append("files", file);
  });
  try {
    let getTokenRequestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      body: form
    };
    // console.log(getTokenRequestOptions)
    // fetch(GLOBAL_URL + 'FileUpload?CaseNumber=' + sessionStorage.getItem('sfCaseNumber'), getTokenRequestOptions);
    return axios.post(DEV_GLOBAL_URL + 'CommunicationServices/FileUpload?CaseNumber=' + sessionStorage.getItem('sfCaseNumber'), form)
    // .then((result: any) => {
    //   console.log(result);
    //   return result;
    // })
    // .catch((ex: any) => {
    //   console.error(ex);
    // });
  } catch (error) {
    console.error('Failed at send teams link, Error: ', error);
  }
};

const saveAdditonalInfo = async (name: any, value: any) => {
  try {
    let getTokenRequestOptions = {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [name]: value })
    };
    // console.log(getTokenRequestOptions)
    if (name === 'IMONumber__c' || name === 'HEXID__c' || name === 'MMSINumber__c') {
      var bodyData = {}
      if (name === 'IMONumber__c') {
        bodyData = JSON.stringify({ Key: 'IMONumber', Value: value, meetingId: sessionStorage.getItem('meetingId') });
      } else if (name === 'HEXID__c') {
        bodyData = JSON.stringify({ Key: 'HexaNumber', Value: value, meetingId: sessionStorage.getItem('meetingId') });
      } else if (name === 'MMSINumber__c') {
        bodyData = JSON.stringify({ Key: 'MMSINumber', Value: value, meetingId: sessionStorage.getItem('meetingId') });
      }
      var config = { headers: { 'Content-Type': 'application/json' } };
      axios.post(DEV_GLOBAL_URL + 'MeetingInformation/StoreAdditionalInformation', bodyData, config)
    }
    return await fetch(DEV_GLOBAL_URL + 'Salesforce/UpdateSalesforceCase?Id=' + sessionStorage.getItem('sfCaseNumber'), getTokenRequestOptions);
  } catch (error) {
    console.error('Failed at send teams link, Error: ', error);
  }
};

const createscheduleMeeting = async (datetime: any, timezone: any, description: any) => {
  // console.log(datetime, timezone, description);
  // sat mar 22,2022 10am
  // var adddatetimehours: any = '';
  // if (timezone === 'EST') {
  //   adddatetimehours = new Date(new Date(datetime).setHours(datetime.getHours() + 5))
  // } else if (timezone === 'EET') {
  //   adddatetimehours = new Date(new Date(datetime).setHours(datetime.getHours() - 2))
  // } else if (timezone === 'CST') {
  //   adddatetimehours = new Date(new Date(datetime).setHours(datetime.getHours() + 6))
  // }
  var time = new Date(datetime);
  var timeCon = time.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
  var d = (new Date(datetime) + '').split(' ');
  d[2] = d[2] + ',';
  var dateCon = d[0] + ' ' + d[1] + ' ' + d[2] + ' ' + d[3];
  // console.log(dateCon, timeCon)
  try {
    var bodyData = JSON.stringify({
      firstName: (localStorageAvailable ? getDisplayFirstNameFromLocalStorage() : ' '),
      lastName: (localStorageAvailable ? getDisplayLastNameFromLocalStorage() : ' '),
      phone: (localStorageAvailable ? getDisplayMobileFromLocalStorage() : ' '),
      email: (localStorageAvailable ? getDisplayEmailFromLocalStorage() : ' '),
      meetingLinkId: window.location.href,
      isQuicklink: false,
      scheduleDateTime: dateCon + ' ' + timeCon,
      Description: description,
      threadId: sessionStorage.getItem('threadId'),
      groupId: sessionStorage.getItem('groupId') || '',
      isExistsMeeting: false,
      CommunicationUserId: sessionStorage.getItem('communicationUserId') ? sessionStorage.getItem('communicationUserId') : createGUID(),
      TimeZone: timezone,
      ServerCallId: ''
    });
    let getTokenRequestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: bodyData
    };
    // console.log(getTokenRequestOptions)
    // return await fetch(GLOBAL_URL + 'CreateMeetingInformation', getTokenRequestOptions);
    var config = { headers: { 'Content-Type': 'application/json' } };
    return axios.post(DEV_GLOBAL_URL + 'MeetingInformation/CreateMeetingInformation', bodyData, config)
  } catch (error) {
    console.error('Failed at send teams link, Error: ', error);
  }
};

const createThread = async (groupId: string, threadId: string, serverCallId: any) => {
  // let threadId: any = await createThreadHelper();
  // console.log(serverCallId, 'serverCallId')
  // let state: State = getState();
  // console.log(state.calls)
  if (threadId) {
    try {
      var bodyData = JSON.stringify({
        firstName: (localStorageAvailable ? getDisplayFirstNameFromLocalStorage() : ' '),
        lastName: (localStorageAvailable ? getDisplayLastNameFromLocalStorage() : ' '),
        phone: (localStorageAvailable ? getDisplayMobileFromLocalStorage() : ' '),
        email: (localStorageAvailable ? getDisplayEmailFromLocalStorage() : ' '),
        meetingLinkId: window.location.href,
        isQuicklink: true,
        scheduleDateTime: '',
        Description: '',
        threadId: threadId,
        groupId: groupId,
        isExistsMeeting: false,
        CommunicationUserId: sessionStorage.getItem('communicationUserId') ? sessionStorage.getItem('communicationUserId') : createGUID(),
        TimeZone: '',
        ServerCallId: serverCallId
      });
      var config = { headers: { 'Content-Type': 'application/json' } };
      return axios.post(DEV_GLOBAL_URL + 'MeetingInformation/CreateMeetingInformation', bodyData, config)
    } catch (error) {
      console.error('Failed at send teams link, Error: ', error);
    }
  } else {
    console.error('unable to generate a new chat thread');
  }
};

const createThreads = async (meetingId: any) => {
  try {
    var bodyData = JSON.stringify({
      firstName: (localStorageAvailable ? getDisplayFirstNameFromLocalStorage() : ' '),
      lastName: (localStorageAvailable ? getDisplayLastNameFromLocalStorage() : ' '),
      phone: (localStorageAvailable ? getDisplayMobileFromLocalStorage() : ' '),
      email: (localStorageAvailable ? getDisplayEmailFromLocalStorage() : ' '),
      isQuicklink: true,
      meetingId: null
    });

    var data = new FormData();
    data.append('firstName', (localStorageAvailable ? getDisplayFirstNameFromLocalStorage() as string : ' '));
    data.append('lastName', (localStorageAvailable ? getDisplayLastNameFromLocalStorage() as string : ' '));
    data.append('phone', (localStorageAvailable ? getDisplayMobileFromLocalStorage() as string : ' '));
    data.append('email', (localStorageAvailable ? getDisplayEmailFromLocalStorage() as string : ' '));
    data.append('isQuicklink', 'true');
    if (meetingId) {
      data.append('meetingLinkId', meetingId);
    }

    var config = { headers: { 'Content-Type': 'application/json' } };
    return axios.post(DEV_GLOBAL_URL + 'MeetingInformation/CreateMeetingInformation', data, config)
  } catch (error) {
    console.error('Failed at send teams link, Error: ', error);
  }
};

const getMeetingInfoData = async (meetingId: any) => {
  try {
    return axios.get(DEV_GLOBAL_URL + 'MeetingInformation/GetMeetingInformation/' + meetingId);
  } catch (error) {
    console.error('Failed at getting environment url, Error: ', error);
  }
};

const createMeetingUserDetails = async (groupId: string, threadId: string) => {
  if (threadId && groupId) {
    const urlParams = new URLSearchParams(window.location.search);
    const meetingId = urlParams.get('meetingId');
    try {
      var bodyData = JSON.stringify({
        firstName: (localStorageAvailable ? getDisplayFirstNameFromLocalStorage() : ' '),
        lastName: (localStorageAvailable ? getDisplayLastNameFromLocalStorage() : ' '),
        phone: (localStorageAvailable ? getDisplayMobileFromLocalStorage() : ' '),
        email: (localStorageAvailable ? getDisplayEmailFromLocalStorage() : ' '),
        meetingLinkId: meetingId,
        isQuicklink: true,
        scheduleDateTime: '',
        Description: '',
        threadId: threadId,
        groupId: groupId,
        isExistsMeeting: true,
        CommunicationUserId: sessionStorage.getItem('communicationUserId') ? sessionStorage.getItem('communicationUserId') : createGUID(),
        TimeZone: '',
        ServerCallId: ''
      });
      var config = { headers: { 'Content-Type': 'application/json' } };
      return axios.post(DEV_GLOBAL_URL + 'MeetingUserDetail/CreateMeetingUserDetails', bodyData, config)
    } catch (error) {
      console.error('Failed at send teams link, Error: ', error);
    }
  } else {
    console.error('unable to generate a new chat thread');
  }
};

const getMeetingUserDetails = async (data: any) => {
  // console.log(data.serverCallId, 'ServerCallId')
  try {
    var config = { headers: { 'Content-Type': 'application/json' } };
    return axios.post(DEV_GLOBAL_URL + 'MeetingUserDetail/GetMeetingUserDetailsByMeetingId', { meetingId: data.meetingId, serverCallId: data.serverCallId }, config)
    // return axios.get(GLOBAL_URL + 'GetMeetingUserDetailsByMeetingId/' + data.meetingId + '/' + data.serverCallId);
  } catch (error) {
    console.error('Failed at getting environment url, Error: ', error);
  }
};

const getMeetingInformationData = async (meetingId: any, userId: any) => {
  try {
    return axios.get(DEV_GLOBAL_URL + 'MeetingInformation/GetMeetingInformation/' + meetingId + '/' + userId);
  } catch (error) {
    console.error('Failed at getting environment url, Error: ', error);
  }
};

const addThreadMember = () => async (dispatch: Dispatch, getState: () => State) => {
  let state: State = getState();
  let user = state.contosoClient.user;
  let threadId = state.thread.threadId ? state.thread.threadId : (sessionStorage.getItem('threadId') || '');

  if (threadId === undefined) {
    console.error('Thread Id not created yet');
    return;
  }
  await addThreadMemberHelper(
    threadId,
    {
      identity: user.identity,
      token: user.token,
      displayName: user.displayName,
      memberRole: 'User'
    },
    dispatch
  );
};

const removeThreadMemberByUserId = (userId: string) => async (dispatch: Dispatch, getState: () => State) => {
  let state: State = getState();
  let chatClient = state.contosoClient.chatClient;
  let threadId = state.thread.threadId ? state.thread.threadId : (sessionStorage.getItem('threadId') || '');
  if (chatClient === undefined) {
    console.error("Chat client doesn't created yet");
    return;
  }
  if (threadId === undefined) {
    console.error('Thread Id not created yet');
    return;
  }
  let chatThreadClient = await chatClient.getChatThreadClient(threadId);
  try {
    await chatThreadClient.removeParticipant({
      communicationUserId: userId
    });
  } catch (error) {
    console.log(error);
  }
};

const getThreadMembers = () => async (dispatch: Dispatch, getState: () => State) => {
  let state: State = getState();
  let chatClient = state.contosoClient.chatClient;
  if (chatClient === undefined) {
    console.error('Chat Client not created yet');
    return;
  }
  let threadId = state.thread.threadId ? state.thread.threadId : (sessionStorage.getItem('threadId') || '');
  if (threadId === undefined) {
    console.error('Thread Id not created yet');
    return;
  }
  let chatThreadClient = await chatClient.getChatThreadClient(threadId);

  try {
    let threadMembers = [];
    for await (let page of chatThreadClient.listParticipants().byPage()) {
      for (const threadMember of page) {
        threadMembers.push(threadMember);
      }
    }
    dispatch(setThreadMembers(threadMembers));
  } catch (error) {
    console.error('Failed at getting members, Error: ', error);
    dispatch(setThreadMembersError(true));
  }
};

// We want to grab everything about the chat thread that has occured before we register for events.
// We care about pre-existing messages, the chat topic, and the participants in this chat
const getThreadInformation = async (chatClient: ChatClient, dispatch: Dispatch, getState: () => State) => {
  let state: State = getState();
  if (chatClient === undefined) {
    console.error('Chat Client not created yet');
    return;
  }
  let threadId = state.thread.threadId ? state.thread.threadId : (sessionStorage.getItem('threadId') || '');
  if (threadId === undefined) {
    console.error('Thread Id not created yet');
    return;
  }

  let chatThreadClient;
  let iteratableParticipants;
  let topic;

  try {
    chatThreadClient = chatClient.getChatThreadClient(threadId);
    iteratableParticipants = chatThreadClient.listParticipants();
  } catch (error) {
    console.error(error);
    dispatch(setThreadMembersError(true));
  }

  let chatParticipants = [];
  // This is just to get all of the members in a chat. This is not performance as we're not using paging
  if (!iteratableParticipants) {
    console.error('unable to resolve chat participant iterator');
    return; // really we need to alert that there was an error?
  }

  for await (const page of iteratableParticipants.byPage()) {
    for (const chatParticipant of page) {
      chatParticipants.push(chatParticipant);
    }
  }

  if (chatParticipants.length === 0) {
    console.error('unable to get members in the thread');
    return;
  }

  // remove undefined display name chat participants
  const validChatParticipants = chatParticipants.filter(
    (chatParticipant) => chatParticipant.displayName !== undefined && chatParticipant.id !== undefined
  );

  // get the emojis for the new participants
  let users = state.contosoClient.users;
  for (var i = 0; i < chatParticipants.length; i++) {
    var threadMember = chatParticipants[i];
    var identity = (threadMember.id as CommunicationUserIdentifier).communicationUserId;
    var user = users[identity];
    if (user == null) {
      // var serverUser = await getEmoji(identity);
      var serverUser = { emoji: "🐱" };
      if (serverUser !== undefined) {
        users[identity] = { emoji: serverUser.emoji };
      }
    }
  }

  const properties = await chatThreadClient?.getProperties();

  if (!properties) {
    console.error('no chat thread properties');
    return;
  }

  dispatch(setThreadId(threadId));
  dispatch(setThreadTopicName(properties.topic));
  dispatch(setContosoUsers(users));
  dispatch(setThreadMembers(validChatParticipants));
};

const updateThreadTopicName = async (
  chatClient: ChatClient,
  threadId: string,
  topicName: string,
  setIsSavingTopicName: React.Dispatch<boolean>
) => {
  const chatThreadClient = await chatClient.getChatThreadClient(threadId);
  updateThreadTopicNameHelper(chatThreadClient, topicName, setIsSavingTopicName);
};

// Thread Helper
const createThreadHelper = async () => {
  try {
    let createThreadRequestOptions = { method: 'GET' };
    let createThreadResponse = await fetch(DEV_GLOBAL_URL + 'CommunicationServices/createThread', createThreadRequestOptions);
    let threadId = await createThreadResponse.text();
    return threadId;
  } catch (error) {
    console.error('Failed at creating thread, Error: ', error);
  }
};

const updateThreadTopicNameHelper = async (
  chatThreadClient: ChatThreadClient,
  topicName: string,
  setIsSavingTopicName: React.Dispatch<boolean>
) => {
  try {
    await chatThreadClient.updateTopic(topicName);
    setIsSavingTopicName(false);
  } catch (error) {
    console.error('Failed at updating thread property, Error: ', error);
  }
};

// Thread Member Helper
const addThreadMemberHelper = async (threadId: string, user: User, dispatch: Dispatch) => {
  try {
    let body = {
      id: user.identity,
      displayName: user.displayName,
      threadId: threadId
    };
    let addMemberRequestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    };
    const urlParams = new URLSearchParams(window.location.search);
    const meetingId = urlParams.get('meetingId');
    // await fetch(GLOBAL_URL + 'addUser/' + threadId, addMemberRequestOptions);
    await fetch(DEV_GLOBAL_URL + 'CommunicationServices/addUser?meetingId=' + meetingId, addMemberRequestOptions);
  } catch (error) {
    console.error('Failed at adding thread member, Error: ', error);
  }
};

// Message Helper
const sendMessageHelper = async (
  chatThreadClient: ChatThreadClient,
  messageContent: string,
  displayName: string,
  clientMessageId: string,
  dispatch: Dispatch,
  getState: () => State
) => {
  // for real time messages we want to store it locally and render it and then sync it with the server message later
  // 1. send the message
  // 2. cache the message locally using the message.id
  // 3. when we get the server synced message we match with the message.id
  try {
    const messageResult = await chatThreadClient.sendMessage(
      { content: messageContent },
      { senderDisplayName: displayName }
    );
    const message: ChatMessage = await chatThreadClient.getMessage(messageResult.id);
    updateMessagesArray(dispatch, getState, convertToClientChatMessage(message, clientMessageId));
  } catch (error) {
    console.error('Failed at getting messages, Error: ', error);
    let failedMessages = getState().chat.failedMessages;
    failedMessages.push(clientMessageId);
    setFailedMessages(failedMessages);

    const message = getState().chat.messages.filter((message) => message.clientMessageId === clientMessageId)[0];
    message.failed = true;
    updateMessagesArray(dispatch, getState, message);
  }
};

// Merge our local messages with server synced messages
const updateMessagesArray = async (dispatch: Dispatch, getState: () => State, newMessage: ClientChatMessage) => {
  let state: State = getState();
  let messages: ClientChatMessage[] = state.chat.messages !== undefined ? state.chat.messages : [];

  // the message id is what we we get from the server when it is synced. There will be other server attributes
  // on the message but the id should be consistent.
  messages = messages.map((message: ClientChatMessage) => {
    return message.clientMessageId === newMessage.clientMessageId ? Object.assign({}, message, newMessage) : message;
  });
  dispatch(setMessages(messages.sort(compareMessages)));
};

const getMessagesHelper = async (chatThreadClient: ChatThreadClient): Promise<ChatMessage[] | undefined> => {
  try {
    let messages: ChatMessage[] = [];
    let getMessagesResponse = await chatThreadClient.listMessages({
      maxPageSize: PAGE_SIZE
    });

    let messages_temp = [];

    for await (let page of getMessagesResponse.byPage()) {
      for (const message of page) {
        messages_temp.push(message);
      }
    }

    while (true) {
      if (messages_temp === undefined) {
        console.error('Unable to get messages from server');
        return;
      }

      // filter and only return top 100 text messages
      messages.push(...messages_temp.filter((message) => message.type === 'text'));
      if (messages.length >= INITIAL_MESSAGES_SIZE) {
        return messages.slice(0, INITIAL_MESSAGES_SIZE);
      }
      // if there is no more messages
      break;
    }

    return messages.slice(0, INITIAL_MESSAGES_SIZE);
  } catch (error) {
    console.error('Failed at getting messages, Error: ', error);
  }
};

// Typing Notification Helper
const sendTypingNotificationHelper = async (chatThreadClient: ChatThreadClient) => {
  try {
    await chatThreadClient.sendTypingNotification();
  } catch (error) {
    console.error('Failed at sending typing notification, Error: ', error);
  }
};



// Token Helper
const getToken = async () => {
  try {
    let getTokenRequestOptions = {
      method: 'POST'
    };
    let getTokenResponse = await fetch(DEV_GLOBAL_URL + 'CommunicationServices/GetToken?isVideoToken=false');
    // let getTokenResponse = await fetch(GLOBAL_URL + 'token', getTokenRequestOptions);
    return getTokenResponse.json().then((_responseJson) => _responseJson);
  } catch (error) {
    console.error('Failed at getting token, Error: ', error);
  }
};

const refreshTokenAsync = async (userIdentity: string): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    return fetch(DEV_GLOBAL_URL + 'CommunicationServices/refreshToken/' + userIdentity).then(
      (response) => {
        if (response.ok) {
          resolve(response.json().then((json) => json.token));
        } else {
          reject(new Error('error'));
        }
      },
      (error) => {
        reject(new Error(error.message));
      }
    );
  });
};

const setEmoji = async (userId: string, emoji: string) => {
  try {
    let getTokenRequestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Emoji: emoji })
    };
    await (await fetch(DEV_GLOBAL_URL + 'userConfig/' + userId, getTokenRequestOptions)).json;
  } catch (error) {
    console.error('Failed at setting emoji, Error: ', error);
  }
};

const getEmoji = async (userId: string) => {
  try {
    let getTokenRequestOptions = {
      headers: { 'Content-Type': 'application/json' },
      method: 'GET'
    };
    return await (await fetch(DEV_GLOBAL_URL + 'userConfig/' + userId, getTokenRequestOptions)).json();
  } catch (error) {
    console.error('Failed at getting emoji, Error: ', error);
  }
};

const sendReadReceipt = (messageId: string) => async (dispatch: Dispatch, getState: () => State) => {
  // This is sent when we get focus to this tab and see this message
  let state: State = getState();
  let chatClient = state.contosoClient.chatClient;
  if (chatClient === undefined) {
    console.error('Chat Client not created yet');
    return;
  }
  let threadId = state.thread.threadId ? state.thread.threadId : (sessionStorage.getItem('threadId') || '');
  if (threadId === undefined) {
    console.error('Thread Id not created yet');
    return;
  }
  await sendReadReceiptHelper(await chatClient.getChatThreadClient(threadId), messageId);
};

const sendReadReceiptHelper = async (chatThreadClient: ChatThreadClient, messageId: string) => {
  let postReadReceiptRequest: SendReadReceiptRequest = {
    chatMessageId: messageId
  };
  await chatThreadClient.sendReadReceipt(postReadReceiptRequest);
};

export {
  sendMessage,
  getMessages,
  createscheduleMeeting,
  saveChatMessages,
  saveSurvey,
  createSendFiles,
  saveAdditonalInfo,
  leaveMeeting,
  createThread,
  createThreads,
  getMeetingInfoData,
  createMeetingUserDetails,
  getMeetingUserDetails,
  getMeetingInformationData,
  addThreadMember,
  getThreadMembers,
  addUserToThread,
  removeThreadMemberByUserId,
  getEmoji,
  setEmoji,
  sendReadReceipt,
  sendTypingNotification,
  updateTypingUsers,
  isValidThread,
  updateThreadTopicName,
  createThreadHelper
};
